<template>
  <AutoFitContainer>
    <div class="dashboard-page">
      <div class="left-panel">
        <h1 class="text-2xl font-bold leading-loose">Monitoring Dashboard</h1>
        <div class="count-list">
          <DarkCard>
            <template #header>
              <div>Total Devices</div>
              <i class="el-icon-cpu"></i>
            </template>
            <template #body>
              <span>{{ devices?.length ?? '--' }}</span>
            </template>
          </DarkCard>
          <DarkCard>
            <template #header>
              <div>Active Connections</div>
              <i class="fa fa-globe"></i>
            </template>
            <template #body>
              <span>{{ connections?.length ?? '--' }}</span>
            </template>
          </DarkCard>
          <ActiveAlerts />
          <AllInsights />
        </div>
        <!--图表-->
        <TimeSeriesData ref="tsDataRef" class="flex-1 mt-4 rounded">
          <template #header-section="{ performanceInfo }">
            <div class="flex flex-row justify-between items-center">
              <h1 class="text-2xl font-bold leading-loose">Time Series Data</h1>
              <div class="flex flex-row">
                {{ performanceInfo.points }}/{{ performanceInfo.totalPoints }} points |
                {{ performanceInfo.renderTime.toFixed(1) }} ms
              </div>
            </div>
          </template>
          <template #buttons>
            <div class="absolute top-4 right-4 flex flex-row gap-2 overflow-hidden">
              <el-tooltip content="Open Time Series Data in a Dialog" placement="top">
                <el-button icon="fa fa-expand" @click="popupTimeSeriesData" />
              </el-tooltip>
            </div>
          </template>
        </TimeSeriesData>
      </div>
      <div class="right-panel">
        <EventLogPanel />
      </div>
    </div>
  </AutoFitContainer>
</template>

<script>
import { getAllAssetVo, getAllConnect, getUserWidgets } from '@/api';
import ActiveAlerts from './components/ActiveAlerts.vue';
import DarkCard from './components/DarkCard.vue';
import EventLogPanel from './components/EventLogPanel.vue';
import AutoFitContainer from '@/components/AutoFitContainer.vue';
import TimeSeriesData from './components/TimeSeriesData.vue';
import AllInsights from './components/AllInsights.vue';

export default {
  name: 'DashboardPage',
  components: {
    DarkCard,
    EventLogPanel,
    AutoFitContainer,
    TimeSeriesData,
    ActiveAlerts,
    AllInsights
  },
  data() {
    return {
      devices: [],
      connections: [],
      alerts: [],
      insights: [],
      /**
       * Example userWidgets data structure:
       * [
       *   {
       *     "userId": "2",
       *     "widgetType": "eventTriggered",
       *     "data": {
       *       "eventCountAnnually": 0,
       *       "eventCountDaily": 0,
       *       "eventCountWeekly": 0,
       *       "eventCountMonthly": 0
       *     }
       *   },
       *   {
       *     "userId": "2",
       *     "widgetType": "eventTrend",
       *     "data": {
       *       "eventTrend": {
       *         "data": [
       *           "0", "0", "0", "0", "0", "0",
       *           "0", "0", "0", "0", "0", "0"
       *         ],
       *         "time": [
       *           "14:55", "15:00", "15:05", "15:10",
       *           "15:15", "15:20", "15:25", "15:30",
       *           "15:35", "15:40", "15:45", "15:50"
       *         ]
       *       }
       *     }
       *   },
       *   {
       *     "userId": "2",
       *     "widgetType": "connected",
       *     "data": {
       *       "connected": 30,
       *       "unit": "data point"
       *     }
       *   },
       *   {
       *     "userId": "2",
       *     "widgetType": "workflowExecutionFailure",
       *     "data": {
       *       "unit": "times",
       *       "workflowExecutionFailure": 4229
       *     }
       *   }
       * ]
       */
      userWidgets: []
    }
  },
  methods: {
    async fetchDevices() {
      const devices = await getAllAssetVo();
      this.devices = devices;
    },
    async fetchConnections() {
      const connections = await getAllConnect();
      this.connections = connections;
    },
    async fetchAlerts() {
      this.alerts = this.userWidgets.filter(widget => widget.widgetType === 'eventTriggered')
        .map(widget => ({
          eventCountAnnually: widget.data.eventCountAnnually,
          eventCountDaily: widget.data.eventCountDaily,
          eventCountWeekly: widget.data.eventCountWeekly,
          eventCountMonthly: widget.data.eventCountMonthly
        }));
      this.alerts = this.alerts.filter(alert => Object.values(alert).some(count => count > 0));
      this.alerts.sort((a, b) => {
        const aTotal = Object.values(a).reduce((sum, count) => sum + count, 0);
        const bTotal = Object.values(b).reduce((sum, count) => sum + count, 0);
        return bTotal - aTotal;
      });
    },
    async fetchUserWidgets() {
      this.userWidgets = await getUserWidgets();
    },
    async popupTimeSeriesData() {
      const padding = 100;
      const tsData = this.$refs.tsDataRef;
      // 获取当前参数
      const selectedDevice = tsData.selectedDevice;
      const selectedMetrics = tsData.selectedMetrics;
      const interval = tsData.selectInterval;
      this.$loaderWindow({
        title: 'Time Series Data',
        width: `calc(100% - ${padding}px)`,
        height: `calc(100% - ${padding}px)`,
        top: `${padding / 2}px`,
        lockScroll: true,
        closeOnClickModal: false,
        component: () => import('./components/TimeSeriesDataDialog.vue'),
        props: {
          device: selectedDevice,
          metrics: selectedMetrics,
          interval: interval
        }
      })
    }
  },
  async mounted() {
    await this.fetchDevices();
    await this.fetchConnections();
    await this.fetchUserWidgets();
    await this.fetchAlerts();
  },
}
</script>
<style scoped>
.dashboard-page {
  padding: 23px;
  flex: 1 1 0;
  display: flex;
  flex-direction: row;
  gap: 20px;
  color: white;
}

.count-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}


.left-panel {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
}

.right-panel {
  flex: none;
  display: flex;
  flex-direction: column;
  width: 400px;
}
</style>
